import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;

    setSending(true);
    try {
      const { error } = await supabase.functions.invoke("send-feedback", {
        body: { name: name.trim(), email: email.trim(), message: message.trim() },
      });

      if (error) throw error;

      toast({ title: "Thank you!", description: "Your feedback has been sent." });
      setName("");
      setEmail("");
      setMessage("");
      onOpenChange(false);
    } catch {
      toast({ title: "Error", description: "Failed to send feedback. Please try again.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Send Feedback</DialogTitle>
          <DialogDescription>We'd love to hear what you think about TopCarib!</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="fb-name">Name</Label>
            <Input id="fb-name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={100} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fb-email">Email</Label>
            <Input id="fb-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={255} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fb-message">Message</Label>
            <Textarea id="fb-message" value={message} onChange={(e) => setMessage(e.target.value)} required maxLength={2000} rows={4} />
          </div>
          <Button type="submit" className="w-full" disabled={sending}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Send feedback
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
